import type {AppendMessage, ThreadMessage} from '@assistant-ui/core';
import {
  useExternalMessageConverter,
  useExternalStoreRuntime,
} from '@assistant-ui/core/react';
import {
  type Dispatch,
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {HttpUserPrompt} from '../client/promptParts';
import {useWbAgentClient} from '../client/useWbAgentClient';
import type {
  ThreadResponse,
  TurnPermissionsPatch,
  TurnResponse,
  WbAgentClient,
} from '../client/WbAgentClient';
import {TERMINAL_STATES} from '../client/WbAgentClient';
import {billingStore} from './billing';
import {buildThreadFromTurns} from './buildThread';
import {draftKey, useDraftStore} from './composer/store';
import {
  publishWBAgentCrossTabEvent,
  useWBAgentCrossTabEvents,
  type WBAgentCrossTabEvent,
} from './crossTabSync';
import type {AgentChatThreadExtras} from './extras';
import {toAUIMessage} from './messageConversions';
import {generateThreadTitle} from './mutations/generateThreadTitle';
import {type PromptContextParams} from './pageContext';
import {
  type BuiltPrompt,
  builtPromptFromStoreContent,
  builtPromptFromText,
  extractText,
  readBuiltPrompt,
  resolveFallbackParentTurnId,
  resolveTurnParent,
  withPromptContext,
} from './promptAssembly';
import {useQueueAdapter} from './queue/adapter';
import {queueKey, useQueueStore} from './queue/store';
import {useQueueDrainer} from './queue/useQueueDrainer';
import {readAgentConfigOverride, readPermissions} from './runConfig';
import {extractErrorMessage, runErrorMessage} from './runtime/errors';
import type {RuntimeEvent} from './runtime/events';
import type {AgentRuntime} from './runtime/runtime';
import type {AgentChatAction} from './store/actions';
import {
  addUserMessage,
  beginLoadThread,
  clearThreadLoad,
  createThread,
  deleteThread as deleteThreadAction,
  failLoadThread,
  loadThread,
  promoteDraft,
  setActiveThread,
  setCancelRequested,
  setThreadError,
  setThreadStatus,
} from './store/actions';
import {
  type AgentChatStore,
  useActiveThread,
  useActiveThreadLoadState,
  useAgentChatDispatch,
  useAgentChatStore,
} from './store/context';
import {getActiveBranch} from './store/messageTree';
import {selectActiveThread, selectThreadById} from './store/selectors';
import {
  hasMeaningfulContent,
  isActiveThread,
  type MessageNode,
  type Thread,
} from './store/thread';
import {StreamingBuffer} from './streamingBuffer';
import {resolveHITLSendBlock} from './tools/catalog';
import {
  notifyWBAgentTurnFinished,
  type WBAgentTurnFinishedEvent,
} from './turnLifeCycle';
import {useAgentChatReadonly} from './useAgentChatReadonly';

const TURN_UPDATED_COALESCE_MS = 350;
const TURN_UPDATED_TERMINAL_REPAIR_MS = 1000;

/**
 * CancelThreadResult reports how `requestCancelThread` resolved: `accepted`
 * when the server confirmed the cancel, `not-running` when there was nothing
 * cancellable (no thread, idle, or already cancelling), and `failed` when the
 * runtime's cancel failed.
 */
export type CancelThreadResult = 'accepted' | 'not-running' | 'failed';

/**
 * useAgentChatRuntime makes an `ExternalStoreRuntime` for `assistant-ui`
 * backed by the agent chat store and the given {@link AgentRuntime} (if ready).
 * `params` is captured on every user message to ground the agent in the
 * caller's current route + page state.
 */
export function useAgentChatRuntime(
  runtime: AgentRuntime | null,
  params: PromptContextParams
): ReturnType<typeof useExternalStoreRuntime> {
  const dispatch = useAgentChatDispatch();
  const activeThread = useActiveThread();
  const {activeThreadId: storeThreadId, isLoading: isActiveThreadLoading} =
    useActiveThreadLoadState();
  const store = useAgentChatStore();
  const crossTabClient = useWbAgentClient();
  const titleClient = useWbAgentClient(params.entityName, params.projectName);
  const runSequenceRef = useRef(0);
  const latestRunTokenRef = useRef(new Map<string, number>());
  const turnUpdatedTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );
  const turnUpdatedRepairTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );

  const isReadonly = useAgentChatReadonly();

  // Lag the thread-list adapter's thread ID by one tick on switch so it changes
  // in sync with real messages, not during the empty-messages transition tick.
  // This avoids a crash in AUI's `setAdapter` [1] where a new internal thread is
  // created from a stale factory before messages are updated.
  //
  // Both renders commit before paint via `useLayoutEffect`, so there's no
  // flicker.
  //
  // [1] https://github.com/assistant-ui/assistant-ui/issues/3652
  const [committedThreadId, setCommittedThreadId] = useState(storeThreadId);
  useLayoutEffect(() => {
    if (committedThreadId !== storeThreadId) {
      setCommittedThreadId(storeThreadId);
    }
  }, [storeThreadId, committedThreadId]);
  const isThreadTransitioning = committedThreadId !== storeThreadId;

  const isRunning =
    activeThread?.status === 'running' && !isThreadTransitioning;

  const activeBranch = useMemo(
    () =>
      activeThread && !isThreadTransitioning
        ? getActiveBranch(activeThread.messageTree)
        : [],
    [activeThread, isThreadTransitioning]
  );
  const threadError =
    activeThread?.status === 'error' ? activeThread.error : undefined;

  const metadata = useMemo(
    () => ({error: threadError ?? undefined}),
    [threadError]
  );

  const convertedMessages = useExternalMessageConverter<MessageNode>({
    callback: toAUIMessage,
    messages: activeBranch,
    isRunning,
    metadata,
  });

  // Used to cancel async generators if the runtime provider unmounts.
  const shutdownRef = useRef(new AbortController());

  useEffect(() => {
    const controller = shutdownRef.current;
    const updateTimers = turnUpdatedTimersRef.current;
    const repairTimers = turnUpdatedRepairTimersRef.current;
    return () => {
      controller.abort();
      for (const timer of updateTimers.values()) {
        clearTimeout(timer);
      }
      for (const timer of repairTimers.values()) {
        clearTimeout(timer);
      }
      updateTimers.clear();
      repairTimers.clear();
    };
  }, []);

  const publishTurnUpdated = useCallback(
    (
      threadId: string,
      turnId: string,
      opts: {immediate?: boolean; scheduleRepair?: boolean} = {}
    ) => {
      const key = `${threadId}:${turnId}`;
      const publish = () =>
        publishWBAgentCrossTabEvent({
          type: 'turn-updated',
          threadId,
          turnId,
        });

      const existingTimer = turnUpdatedTimersRef.current.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
        turnUpdatedTimersRef.current.delete(key);
      }

      if (opts.immediate) {
        publish();
      } else {
        turnUpdatedTimersRef.current.set(
          key,
          setTimeout(() => {
            turnUpdatedTimersRef.current.delete(key);
            publish();
          }, TURN_UPDATED_COALESCE_MS)
        );
      }

      if (opts.scheduleRepair) {
        const existingRepairTimer = turnUpdatedRepairTimersRef.current.get(key);
        if (existingRepairTimer) {
          clearTimeout(existingRepairTimer);
        }
        turnUpdatedRepairTimersRef.current.set(
          key,
          setTimeout(() => {
            turnUpdatedRepairTimersRef.current.delete(key);
            publish();
          }, TURN_UPDATED_TERMINAL_REPAIR_MS)
        );
      }
    },
    []
  );

  const drainEvents = useCallback(
    async (
      threadId: string,
      generator: AsyncGenerator<RuntimeEvent, void, undefined>,
      opts: {initialTurnId?: string} = {}
    ): Promise<{
      outcome: 'idle' | 'error' | 'cancelled';
      threadId: string;
      runId: string;
    }> => {
      // Scope every dispatch to the run that produced it. The reducer drops
      // events whose runId already has an outcome in thread.runOutcomes.
      let runId = '';
      let currentTurnId = opts.initialTurnId ?? '';
      let currentThreadId = threadId;
      const buffer = new StreamingBuffer(event => {
        dispatch({
          type: 'applyRuntimeEvent',
          threadId: currentThreadId,
          event,
          runId,
          timestamp: Date.now(),
        });
      });

      let outcome: 'idle' | 'error' | 'cancelled' = 'idle';
      try {
        for await (const event of generator) {
          if (event.type === 'run-started') {
            runId = event.runId;
          }
          if (event.type === 'turn-created') {
            currentTurnId = event.turnId;
            publishWBAgentCrossTabEvent({
              type: 'turn-created',
              turnId: event.turnId,
              threadId: event.threadId,
            });
          }
          if (event.type === 'run-error') {
            outcome = 'error';
          }
          if (event.type === 'run-cancelled') {
            outcome = 'cancelled';
          }
          if (event.type === 'turn-created') {
            if (currentThreadId !== event.threadId) {
              dispatch(promoteDraft(currentThreadId, event.threadId));
              useDraftStore
                .getState()
                .promote(draftKey(currentThreadId), event.threadId);
              useQueueStore
                .getState()
                .promote(queueKey(currentThreadId), event.threadId);
              currentThreadId = event.threadId;
              // Promotion only happens on a thread's first turn, so this is
              // the moment to title it. Runs in the background and never
              // blocks the stream; failures fall back to the prompt snippet.
              autoGenerateThreadTitle(
                titleClient,
                dispatch,
                store,
                currentThreadId
              );
            }
            useQueueStore.getState().confirmDrained(queueKey(currentThreadId));
          }
          buffer.push(event);
          if (currentTurnId && isRuntimeContentUpdateEvent(event)) {
            publishTurnUpdated(currentThreadId, currentTurnId);
          }
          if (currentTurnId && isRuntimeTerminalEvent(event)) {
            publishTurnUpdated(currentThreadId, currentTurnId, {
              immediate: true,
              scheduleRepair: true,
            });
          }
        }
      } finally {
        buffer.dispose();
      }

      return {outcome, threadId: currentThreadId, runId};
    },
    [dispatch, publishTurnUpdated, store, titleClient]
  );

  const withStatusDispatch = useCallback(
    async (
      threadId: string,
      run: () => Promise<{
        outcome: 'idle' | 'error' | 'cancelled';
        threadId: string;
        runId: string;
      }>
    ): Promise<void> => {
      const runToken = ++runSequenceRef.current;
      latestRunTokenRef.current.set(threadId, runToken);
      dispatch(setThreadStatus(threadId, 'running'));
      dispatch(setThreadError(threadId, null));

      // The id can rotate mid-run when a draft is promoted (see drainEvents).
      // Track the latest id so trailing dispatches land on the right thread.
      let currentThreadId = threadId;
      let settledOutcome: WBAgentTurnFinishedEvent['outcome'] | null = null;
      try {
        const {outcome, threadId: finalThreadId, runId} = await run();
        currentThreadId = finalThreadId;
        settledOutcome = outcome;
        claimThreadRunToken(
          latestRunTokenRef.current,
          currentThreadId,
          runToken
        );
        if (outcome === 'cancelled') {
          return;
        }
        if (
          outcome !== 'error' &&
          shouldApplyTrailingStatus(
            store,
            currentThreadId,
            runId,
            runToken,
            latestRunTokenRef.current
          )
        ) {
          dispatch(setThreadStatus(currentThreadId, 'idle'));
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          if (
            shouldApplyTrailingStatus(
              store,
              currentThreadId,
              '',
              runToken,
              latestRunTokenRef.current
            )
          ) {
            dispatch(setThreadStatus(currentThreadId, 'idle'));
          }
        } else {
          dispatch(setThreadError(currentThreadId, runErrorMessage(err)));
          dispatch(setThreadStatus(currentThreadId, 'error'));
          settledOutcome = 'error';
        }
      } finally {
        if (settledOutcome) {
          notifyWBAgentTurnFinished({
            threadId: currentThreadId,
            outcome: settledOutcome,
          });
        }
        // A settled turn may have spent quota; refresh billing state.
        billingStore.refresh();
      }
    },
    [dispatch, store]
  );

  const startTurn = useCallback(
    async (
      threadId: string,
      opts: {
        userPrompt?: HttpUserPrompt;
        parentId?: string | null;
        agentConfigOverride?: string;
        permissions?: TurnPermissionsPatch;
        parentTurnId?: string | null;
      }
    ) => {
      if (!runtime) {
        dispatch(setThreadError(threadId, null));
        dispatch(setThreadStatus(threadId, 'idle'));
        return;
      }

      // Used as a parent fallback in the runtime when `parentId` doesn't yield
      // a turn-encoded ancestor (e.g. the previous turn was cancelled before
      // any extractable nodes were created). `null` on a brand-new draft;
      // `undefined` past it.
      const fallbackParentTurnId =
        selectThreadById(store.getState(), threadId)?.latestTurnId ?? undefined;

      await withStatusDispatch(threadId, async () => {
        return drainEvents(
          threadId,
          runtime.runTurn({
            threadId,
            userPrompt: opts.userPrompt,
            parentId: opts.parentId,
            fallbackParentTurnId,
            agentConfigOverride: opts.agentConfigOverride,
            permissions: opts.permissions,
            parentTurnId: opts.parentTurnId,
            signal: shutdownRef.current.signal,
          })
        );
      });
    },
    [dispatch, runtime, shutdownRef, drainEvents, withStatusDispatch, store]
  );

  const requestCancelThread = useCallback(
    async (threadId: string): Promise<CancelThreadResult> => {
      const thread = selectThreadById(store.getState(), threadId);
      // Only a running turn is cancellable; ignore repeat clicks (and AUI's
      // cancel-on-escape) once a cancel is already pending.
      if (!thread || thread.status !== 'running' || thread.cancelRequested) {
        return 'not-running';
      }

      dispatch(setCancelRequested(threadId, true));
      // Fallback for a tab with no local operation (a hydrated follower),
      // letting the runtime cancel by turn ID instead of returning null.
      const knownTurnId = thread.latestTurnId ?? undefined;
      const cancelledTurnId =
        (await runtime?.requestCancel?.(threadId, knownTurnId)) ?? null;
      if (cancelledTurnId == null) {
        dispatch(setCancelRequested(threadId, false));
        return 'failed';
      }

      // Broadcast only the server-confirmed cancel, so follower tabs latch
      // their cancelling state without ever needing a retraction.
      const storeThread = selectThreadById(store.getState(), threadId);
      const broadcastThreadId =
        storeThread?.latestTurnId == null && cancelledTurnId != null
          ? cancelledTurnId
          : (storeThread?.id ?? threadId);
      publishWBAgentCrossTabEvent({
        type: 'turn-cancel-requested',
        threadId: broadcastThreadId,
        turnId: cancelledTurnId,
      });
      return 'accepted';
    },
    [runtime, dispatch, store]
  );

  const switchToThread = useSwitchToThread(
    runtime,
    dispatch,
    store,
    shutdownRef,
    drainEvents,
    withStatusDispatch
  );

  useHydrateCrossTabThread(
    crossTabClient,
    runtime,
    dispatch,
    store,
    shutdownRef,
    drainEvents,
    withStatusDispatch
  );

  const {appendAndRunTurn, onNew, onEdit, onReload, onCancel} =
    useThreadCallbacks(
      store,
      dispatch,
      startTurn,
      requestCancelThread,
      params,
      isReadonly
    );

  const setMessages = useCallback(
    (messages: readonly ThreadMessage[]) => {
      const thread = selectActiveThread(store.getState());
      const threadId = thread?.id;
      if (!threadId || messages.length === 0) {
        return;
      }

      // TODO: When branch-switching is surfaced, delete this guard and anchor
      // assistant insertion to the run's turn, not currentNodeId. Otherwise it
      // drops a user's mid-stream branch switch. Inert today (linear tree).
      if (thread.status === 'running') {
        return;
      }

      const targetNodeId = messages[messages.length - 1].id;
      if (targetNodeId !== thread.messageTree.currentNodeId) {
        dispatch({
          type: 'switchBranch',
          timestamp: Date.now(),
          targetNodeId,
          threadId,
        });
      }
    },
    [dispatch, store]
  );

  // Switch the thread-list ID in sync with real messages, not during the
  // empty-messages transition tick; see above comment.
  const threadListAdapter = useMemo(
    () => ({
      // AUI uses `??` to fall back to DEFAULT_THREAD_ID; '' bypasses that
      // fallback and corrupts AUI's thread-list state. Reachable when
      // switching from a summary history entry before its turns have loaded.
      threadId: committedThreadId ?? undefined,
      threads: committedThreadId
        ? [{id: committedThreadId, status: 'regular' as const}]
        : [],
      onSwitchToNewThread: () => dispatch(createThread()),
      onSwitchToThread: switchToThread,
    }),
    [committedThreadId, dispatch, switchToThread]
  );

  const threadExtras = useMemo<AgentChatThreadExtras>(
    () => ({
      // Surface 'switchToThread' so the error UI can retry a failed load; AUI's
      // own 'switchToThread' no-ops for the active thread.
      reloadThread: switchToThread,
    }),
    [switchToThread]
  );

  useQueueDrainer(store, appendAndRunTurn, params.legacyWBMatchParams);
  const queueAdapter = useQueueAdapter(
    store,
    activeThread,
    params,
    onNew,
    requestCancelThread
  );

  const auiRuntime = useExternalStoreRuntime({
    isDisabled: isActiveThreadLoading || isReadonly,
    messages: convertedMessages,
    setMessages,
    isRunning,
    onNew,
    onEdit,
    onReload,
    onCancel,
    extras: threadExtras,
    queue: queueAdapter,
    adapters: {
      threadList: threadListAdapter,
    },
  });

  return auiRuntime;
}

type DrainEvents = (
  threadId: string,
  generator: AsyncGenerator<RuntimeEvent, void, undefined>,
  opts?: {initialTurnId?: string}
) => Promise<{
  outcome: 'idle' | 'error' | 'cancelled';
  threadId: string;
  runId: string;
}>;

type WithStatusDispatch = (
  threadId: string,
  run: () => Promise<{
    outcome: 'idle' | 'error' | 'cancelled';
    threadId: string;
    runId: string;
  }>
) => Promise<void>;

async function fetchThreadMetadata(
  client: WbAgentClient,
  threadId: string,
  signal: AbortSignal
): Promise<ThreadResponse | undefined> {
  try {
    return await client.get<ThreadResponse>(`/threads/${threadId}`, {}, signal);
  } catch {
    return undefined;
  }
}

/**
 * autoGenerateThreadTitle kicks off AI title generation for a freshly promoted
 * thread. Skipped when the thread already carries a title (e.g. renamed while
 * still a draft) or when no client is available. Fire-and-forget: the shared
 * abort map lets a later manual rename supersede it.
 */
function autoGenerateThreadTitle(
  client: WbAgentClient | null,
  dispatch: Dispatch<AgentChatAction>,
  store: AgentChatStore,
  threadId: string
): void {
  if (!client) {
    return;
  }
  const thread = selectThreadById(store.getState(), threadId);
  if (!thread || thread.title) {
    return;
  }
  void generateThreadTitle({
    client,
    dispatch,
    store,
    threadId,
    notifyOnError: false,
  });
}

/**
 * claimThreadRunToken records `runToken` as the newest run on `threadId`,
 * carrying a run's claim across the id rotation a promotion causes. Tokens only
 * ever rise, so a run that has already been superseded cannot take the claim
 * back from the run that replaced it.
 */
function claimThreadRunToken(
  latestRunTokens: Map<string, number>,
  threadId: string,
  runToken: number
): void {
  const claimed = latestRunTokens.get(threadId);
  if (claimed == null || claimed < runToken) {
    latestRunTokens.set(threadId, runToken);
  }
}

function shouldApplyTrailingStatus(
  store: AgentChatStore,
  threadId: string,
  runId: string,
  runToken: number,
  latestRunTokens: Map<string, number>
): boolean {
  const thread = selectThreadById(store.getState(), threadId);
  if (!thread || latestRunTokens.get(threadId) !== runToken) {
    return false;
  }
  return (
    !thread.currentRunId || (runId !== '' && thread.currentRunId === runId)
  );
}

interface HydrationRequest {
  shouldResume: boolean;
  force?: boolean;
  turnId?: string | null;
}

function useHydrateCrossTabThread(
  client: ReturnType<typeof useWbAgentClient>,
  runtime: AgentRuntime | null,
  dispatch: Dispatch<AgentChatAction>,
  store: AgentChatStore,
  shutdownRef: MutableRefObject<AbortController>,
  drainEvents: DrainEvents,
  withStatusDispatch: WithStatusDispatch
): void {
  const fetchControllersRef = useRef(new Map<string, AbortController>());
  const hydratingThreadsRef = useRef(new Set<string>());
  const queuedHydrationsRef = useRef(new Map<string, HydrationRequest>());

  useEffect(() => {
    const controllers = fetchControllersRef.current;
    const hydratingThreads = hydratingThreadsRef.current;
    const queuedHydrations = queuedHydrationsRef.current;
    return () => {
      for (const controller of controllers.values()) {
        controller.abort();
      }
      controllers.clear();
      hydratingThreads.clear();
      queuedHydrations.clear();
    };
  }, []);

  const fetchAndLoadThread = useCallback(
    async (threadId: string, request: HydrationRequest) => {
      if (!client) {
        return;
      }

      const priorController = fetchControllersRef.current.get(threadId);
      priorController?.abort();

      const controller = new AbortController();
      fetchControllersRef.current.set(threadId, controller);

      const abortOnShutdown = () => controller.abort();
      shutdownRef.current.signal.addEventListener('abort', abortOnShutdown, {
        once: true,
      });

      try {
        const [turns, threadMetadata] = await Promise.all([
          client.get(
            '/turns',
            {
              entity: undefined,
              project: undefined,
              thread_id: threadId,
              sort_by: 'created_at',
              sort_desc: false,
              limit: 100,
            },
            controller.signal
          ),
          fetchThreadMetadata(client, threadId, controller.signal),
        ]);
        if (controller.signal.aborted) {
          return;
        }
        if (turns.length === 0) {
          return;
        }

        // NOTE: Read before `loadThread`, which resets `currentRunId`.
        const priorThread = selectThreadById(store.getState(), threadId);

        // Re-checked after the fetch, since a stream can start mid-request.
        // Loading a snapshot over a live stream strands its terminal event.
        if (
          shouldKeepLiveRun(priorThread, turns) ||
          isStaleSnapshot(priorThread, turns)
        ) {
          return;
        }

        const hadLiveRun = hasLiveRun(priorThread);

        const thread = buildThreadFromTurns(threadId, turns, threadMetadata);
        dispatch(loadThread(thread));
        if (fetchControllersRef.current.get(threadId) === controller) {
          fetchControllersRef.current.delete(threadId);
        }

        // This 'running' came from server history, not from a stream this tab
        // owns, so no terminal event will arrive to clear it and the loader
        // would spin forever. Start a poll, unless this tab already has a
        // live run that resuming would abort.
        if (
          (request.shouldResume || !hadLiveRun) &&
          thread.status === 'running' &&
          runtime?.resumeTurn
        ) {
          const lastTurn = turns[turns.length - 1];
          void withStatusDispatch(threadId, async () => {
            const gen = runtime.resumeTurn!({
              turnId: lastTurn.id,
              threadId: threadId,
              signal: shutdownRef.current.signal,
              initialSnapshot: lastTurn,
            });
            return drainEvents(threadId, gen, {initialTurnId: lastTurn.id});
          });
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }
        // A background repair failure must not preempt an in-flight foreground
        // load, which owns the spinner and will report its own outcome.
        if (store.getState().threadLoads[threadId]?.status === 'loading') {
          return;
        }
        dispatch(
          failLoadThread(threadId, {
            kind: 'error',
            message: extractErrorMessage(err),
          })
        );
      } finally {
        shutdownRef.current.signal.removeEventListener(
          'abort',
          abortOnShutdown
        );
        if (fetchControllersRef.current.get(threadId) === controller) {
          fetchControllersRef.current.delete(threadId);
        }
      }
    },
    [
      client,
      dispatch,
      drainEvents,
      runtime,
      shutdownRef,
      store,
      withStatusDispatch,
    ]
  );

  // Cross-tab events are invalidation signals, not an ordered runtime-event
  // stream. Followers refetch the authoritative thread snapshot and may resume
  // an in-progress turn. This is intentionally simpler than a Web Locks leader
  // model: duplicate broadcasts are idempotent, a missed broadcast is repaired
  // by later history refetches, and a crashed origin tab can be recovered by a
  // follower that sees or loads the running turn.
  const hydrateThread = useCallback(
    (threadId: string, request: HydrationRequest) => {
      if (!client) {
        return;
      }
      if (shouldSkipHydration(store, threadId, request)) {
        return;
      }

      if (hydratingThreadsRef.current.has(threadId)) {
        queuedHydrationsRef.current.set(
          threadId,
          mergeHydrationRequest(
            queuedHydrationsRef.current.get(threadId),
            request
          )
        );
        return;
      }

      void (async () => {
        hydratingThreadsRef.current.add(threadId);
        let currentRequest: HydrationRequest | undefined = request;

        try {
          while (currentRequest) {
            queuedHydrationsRef.current.delete(threadId);
            if (!shouldSkipHydration(store, threadId, currentRequest)) {
              await fetchAndLoadThread(threadId, currentRequest);
            }
            currentRequest = queuedHydrationsRef.current.get(threadId);
          }
        } finally {
          hydratingThreadsRef.current.delete(threadId);
        }
      })();
    },
    [client, fetchAndLoadThread, store]
  );

  const handleCrossTabEvent = useCallback(
    (event: WBAgentCrossTabEvent) => {
      if (event.type === 'thread-deleted') {
        const existingThread = selectThreadById(
          store.getState(),
          event.threadId
        );
        if (existingThread) {
          dispatch(deleteThreadAction(existingThread.id));
        }
        useQueueStore.getState().clear(queueKey(event.threadId));
        return;
      }

      if (event.type === 'turn-cancel-requested') {
        const existingThread = selectThreadById(
          store.getState(),
          event.threadId
        );
        if (
          existingThread &&
          existingThread.status === 'running' &&
          existingThread.latestTurnId === event.turnId &&
          !existingThread.cancelRequested
        ) {
          dispatch(setCancelRequested(existingThread.id, true));
        }
        return;
      }

      hydrateThread(event.threadId, {
        shouldResume: event.type === 'turn-created',
        force: event.type === 'turn-updated',
        turnId:
          event.type === 'turn-created' || event.type === 'turn-updated'
            ? (event.turnId ?? null)
            : null,
      });
    },
    [dispatch, hydrateThread, store]
  );

  useWBAgentCrossTabEvents(handleCrossTabEvent);
}

function mergeHydrationRequest(
  current: HydrationRequest | undefined,
  next: HydrationRequest
): HydrationRequest {
  return {
    shouldResume: Boolean(current?.shouldResume || next.shouldResume),
    force: Boolean(current?.force || next.force),
    turnId: next.turnId ?? current?.turnId ?? null,
  };
}

function shouldSkipHydration(
  store: AgentChatStore,
  threadId: string,
  request: HydrationRequest
): boolean {
  if (!request.turnId) {
    return false;
  }
  const existing = selectThreadById(store.getState(), threadId);
  if (request.force) {
    // A remote repair signal should not replace the message tree while this tab
    // is actively applying poll events for that same turn. Non-running threads
    // still hydrate so missed terminal content can be repaired.
    return (
      existing?.loadStatus === 'loaded' &&
      existing.status === 'running' &&
      existing.currentRunId != null &&
      existing.latestTurnId === request.turnId
    );
  }
  return (
    existing?.loadStatus === 'loaded' &&
    existing.latestTurnId === request.turnId
  );
}

/**
 * hasLiveRun reports whether this tab is itself streaming or polling a run on
 * `thread`. Only a live run sets `currentRunId`, so a follower that only
 * hydrated a snapshot never qualifies.
 */
function hasLiveRun(thread: Thread | undefined): thread is Thread {
  return thread?.status === 'running' && thread.currentRunId != null;
}

/**
 * shouldKeepLiveRun reports whether loading `turns` would strand this tab's
 * live run, either by dropping the run id its terminal event needs or by
 * replacing message content the snapshot does not have yet. Only a run whose
 * turn the snapshot has both settled and moved past is safe to load over.
 */
function shouldKeepLiveRun(
  thread: Thread | undefined,
  turns: TurnResponse[]
): boolean {
  if (!hasLiveRun(thread)) {
    return false;
  }

  const ownTurn = turns.find(turn => turn.id === thread.latestTurnId);
  // A run that has not reported its turn yet, or whose turn the snapshot
  // predates, is ahead of the snapshot rather than behind it.
  if (ownTurn == null) {
    return true;
  }

  // `latestTurnId` still naming a turn this tab watched settle means the live
  // run has yet to report its own, so it is ahead of the snapshot as well.
  if (thread.turnOutcomes[ownTurn.id] != null) {
    return true;
  }

  return (
    !TERMINAL_STATES.includes(ownTurn.state) ||
    ownTurn.id === turns[turns.length - 1].id
  );
}

/**
 * isStaleSnapshot reports whether `turns` is behind what this tab has already
 * watched settle. A turn never returns to running once it ends, so a snapshot
 * still reporting one in progress would revive a finished turn. Unlike
 * {@link shouldKeepLiveRun} this holds whether or not a run is live. This
 * function expects a non-empty `turns` snapshot.
 */
function isStaleSnapshot(
  thread: Thread | undefined,
  turns: TurnResponse[]
): boolean {
  const latest = turns[turns.length - 1];
  return (
    !TERMINAL_STATES.includes(latest.state) &&
    thread?.turnOutcomes[latest.id] != null
  );
}

function isRuntimeContentUpdateEvent(event: RuntimeEvent): boolean {
  switch (event.type) {
    case 'user-message':
    case 'text-message-start':
    case 'text-message-content':
    case 'text-message-end':
    case 'reasoning-start':
    case 'reasoning-content':
    case 'reasoning-end':
    case 'assistant-content-replace':
    case 'tool-call-start':
    case 'tool-call-args':
    case 'tool-call-end':
    case 'tool-call-result':
    case 'agent-questions':
      return true;
    default:
      return false;
  }
}

function isRuntimeTerminalEvent(event: RuntimeEvent): boolean {
  return (
    event.type === 'run-finished' ||
    event.type === 'run-error' ||
    event.type === 'run-cancelled'
  );
}

/**
 * useSwitchToThread activates a thread by id, loading it from history first
 * when it isn't already loaded. Safe to call repeatedly or mid-load; a running
 * thread resumes polling once loaded.
 */
function useSwitchToThread(
  runtime: AgentRuntime | null,
  dispatch: Dispatch<AgentChatAction>,
  store: AgentChatStore,
  shutdownRef: MutableRefObject<AbortController>,
  drainEvents: DrainEvents,
  withStatusDispatch: WithStatusDispatch
): (threadId: string) => Promise<void> {
  // Thread switch may target a thread in any project; scope is overridden
  // per call below.
  const client = useWbAgentClient();

  return useCallback(
    async (threadId: string) => {
      const state = store.getState();
      const existing = selectThreadById(state, threadId);
      if (
        existing &&
        existing.loadStatus === 'loaded' &&
        existing.status !== 'running'
      ) {
        dispatch(setActiveThread(existing.id));
        return;
      }

      if (!client) {
        return;
      }

      // Join an in-flight load for this same thread instead of refetching; the
      // spinner rides the existing fetch, so just re-activate the thread.
      if (store.getState().threadLoads[threadId]?.status === 'loading') {
        dispatch(setActiveThread(threadId));
        return;
      }

      // Not aborted on switch-away, so a backgrounded fetch runs to completion
      // and caches; only unmount aborts it. A 'loading' entry must always have a
      // live fetch behind it, so any future abort path has to clear that entry
      // too or a spinner strands.
      const controller = new AbortController();

      const abortOnShutdown = () => controller.abort();
      shutdownRef.current.signal.addEventListener('abort', abortOnShutdown, {
        once: true,
      });

      dispatch(setActiveThread(threadId));
      dispatch(beginLoadThread(threadId));

      try {
        const [turns, threadMetadata] = await Promise.all([
          client.get(
            '/turns',
            {
              // Fetch across all projects the user can read; the thread may live
              // in a project different from the current route.
              entity: undefined,
              project: undefined,
              thread_id: threadId,
              sort_by: 'created_at',
              sort_desc: false,
              // TODO: Handle pagination for turns.
              limit: 100,
            },
            controller.signal
          ),
          fetchThreadMetadata(client, threadId, controller.signal),
        ]);

        // A committed thread always has its root turn, so no turns means the
        // conversation was archived, deleted, or is no longer readable.
        if (turns.length === 0) {
          dispatch(failLoadThread(threadId, {kind: 'not-found'}));
          return;
        }

        // The fetch above races this tab's own turn creation, so `turns` can
        // omit a turn still streaming here. Loading it would drop that turn
        // from the transcript, so keep what is on screen and let the live run
        // finish.
        const priorThread = selectThreadById(store.getState(), threadId);
        if (
          shouldKeepLiveRun(priorThread, turns) ||
          isStaleSnapshot(priorThread, turns)
        ) {
          dispatch(clearThreadLoad(threadId));
          return;
        }

        const thread = buildThreadFromTurns(threadId, turns, threadMetadata);
        // The thread isn't in the store until `loadThread` below, so
        // `selectActiveThread` returns undefined; compare ids instead.
        const isStillActive = isActiveThread(store.getState(), threadId);

        // A running thread cached after switching away has no live poll and
        // would strand as "running". Drop it so a later switch-back re-fetches
        // and resumes.
        if (thread.status === 'running' && !isStillActive) {
          dispatch(clearThreadLoad(threadId));
          return;
        }

        dispatch(loadThread(thread));

        // Resume the still-running turn now its load finished, tied to unmount.
        if (
          thread.status === 'running' &&
          runtime?.resumeTurn &&
          isStillActive
        ) {
          const lastTurn = turns[turns.length - 1];
          void withStatusDispatch(threadId, async () => {
            const gen = runtime.resumeTurn!({
              turnId: lastTurn.id,
              threadId: threadId,
              signal: shutdownRef.current.signal,
              initialSnapshot: lastTurn,
            });
            return drainEvents(threadId, gen, {initialTurnId: lastTurn.id});
          });
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          // Unmount aborts this fetch but the store can outlive the runtime,
          // so clear the load or the spinner would stick on remount.
          dispatch(clearThreadLoad(threadId));
          return;
        }
        dispatch(
          failLoadThread(threadId, {
            kind: 'error',
            message: extractErrorMessage(err),
          })
        );
      } finally {
        shutdownRef.current.signal.removeEventListener(
          'abort',
          abortOnShutdown
        );
      }
    },
    [
      client,
      runtime,
      dispatch,
      store,
      shutdownRef,
      drainEvents,
      withStatusDispatch,
    ]
  );
}

type StartTurn = (
  threadId: string,
  opts: {
    userPrompt?: HttpUserPrompt;
    parentId?: string | null;
    agentConfigOverride?: string;
    permissions?: TurnPermissionsPatch;
    parentTurnId?: string | null;
  }
) => Promise<void>;

export type AppendAndRunTurn = (
  threadId: string,
  prompt: BuiltPrompt,
  parentId: string | null,
  agentConfigOverride?: string,
  parentTurnId?: string | null,
  permissions?: TurnPermissionsPatch
) => Promise<void>;

/**
 * readToolContinuation reports whether the append responds to the thread's
 * pending HITL interaction (marked in the append's `runConfig.custom`), so the
 * pending-interaction block in `onNew` lets it through.
 */
function readToolContinuation(message: AppendMessage): boolean {
  return message.runConfig?.custom?.toolContinuation === true;
}

/**
 * isSendBlockedByHITLTool reports whether the active thread's pending HITL tool
 * interaction blocks every send path (composer and programmatic), leaf-anchored
 * so a continuation turn that has already moved the leaf self-heals instead of
 * staying stuck.
 */
function isSendBlockedByHITLTool(store: AgentChatStore): boolean {
  return resolveHITLSendBlock(selectActiveThread(store.getState()));
}

/**
 * useThreadCallbacks creates all four AUI thread callbacks: `onNew`, `onEdit`,
 * `onReload`, and `onCancel`. `params` is the current page scope and path,
 * captured on each user message to build prompt context. When `isReadonly`, the
 * three write callbacks no-op; `onCancel` stays live so an in-flight turn can
 * still be stopped.
 */
function useThreadCallbacks(
  store: AgentChatStore,
  dispatch: Dispatch<AgentChatAction>,
  startTurn: StartTurn,
  requestCancelThread: (threadId: string) => Promise<CancelThreadResult>,
  params: PromptContextParams,
  isReadonly: boolean
) {
  const appendAndRunTurn = useCallback<AppendAndRunTurn>(
    async (
      threadId,
      prompt,
      parentId,
      agentConfigOverride,
      parentTurnId,
      permissions
    ) => {
      dispatch(addUserMessage(threadId, parentId, prompt.storeParts));
      await startTurn(threadId, {
        userPrompt: prompt.httpParts,
        parentId,
        agentConfigOverride,
        permissions,
        parentTurnId,
      });
    },
    [dispatch, startTurn]
  );

  // Hold params in a ref so the callback identities stay stable across route
  // changes; otherwise AUI re-binds on every navigation.
  const paramsRef = useRef(params);
  // oxlint-disable-next-line react/refs
  paramsRef.current = params;

  const onNew = useCallback(
    async (message: AppendMessage) => {
      if (isReadonly) {
        return;
      }

      // Block every send path (composer and programmatic) while a pending
      // interaction requires an explicit response; the response itself is
      // marked as a tool continuation and passes.
      if (isSendBlockedByHITLTool(store) && !readToolContinuation(message)) {
        return;
      }

      const userBuilt =
        readBuiltPrompt(message) ?? builtPromptFromText(extractText(message));

      if (!hasMeaningfulContent(userBuilt.storeParts)) {
        return;
      }

      const currentThread = selectActiveThread(store.getState());
      let threadId = currentThread?.id ?? null;
      if (!threadId) {
        const action = createThread();
        threadId = action.id;
        dispatch(action);
      }

      const {parentId, parentTurnId} = resolveTurnParent(currentThread);
      await appendAndRunTurn(
        threadId,
        withPromptContext(userBuilt, paramsRef.current),
        parentId,
        readAgentConfigOverride(message),
        parentTurnId,
        readPermissions(message)
      );
    },
    [dispatch, appendAndRunTurn, store, isReadonly]
  );

  const onEdit = useCallback(
    async (message: AppendMessage) => {
      if (isReadonly) {
        return;
      }

      const threadId = selectActiveThread(store.getState())?.id;
      if (!threadId) {
        return;
      }

      if (isSendBlockedByHITLTool(store)) {
        return;
      }

      const userBuilt =
        readBuiltPrompt(message) ?? builtPromptFromText(extractText(message));

      if (!hasMeaningfulContent(userBuilt.storeParts)) {
        return;
      }

      await appendAndRunTurn(
        threadId,
        withPromptContext(userBuilt, paramsRef.current),
        message.parentId ?? null,
        readAgentConfigOverride(message),
        undefined,
        readPermissions(message)
      );
    },
    [appendAndRunTurn, store, isReadonly]
  );

  const onReload = useCallback(
    async (parentId: string | null, _config?: unknown) => {
      if (isReadonly) {
        return;
      }

      const thread = selectActiveThread(store.getState());
      if (!thread) {
        return;
      }

      if (isSendBlockedByHITLTool(store)) {
        return;
      }

      const node = parentId ? thread.messageTree.nodes[parentId] : undefined;
      const prompt =
        node?.role === 'user'
          ? builtPromptFromStoreContent(node.content)
          : undefined;

      await startTurn(thread.id, {
        userPrompt: prompt?.httpParts,
        parentId,
        parentTurnId: resolveFallbackParentTurnId(thread, parentId),
      });
    },
    [startTurn, store, isReadonly]
  );

  const onCancel = useCallback(async () => {
    const threadId = selectActiveThread(store.getState())?.id;
    if (threadId) {
      await requestCancelThread(threadId);
    }
  }, [requestCancelThread, store]);

  return {appendAndRunTurn, onNew, onEdit, onReload, onCancel};
}
